import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TabEnumFe } from "@intelligems/ig-types";
import {
  RolloutExperienceRequestBodyType,
  RolloutExperienceRequestPathParamType,
  RolloutExperienceResponseBodyType,
} from "@intelligems/ig-types/functions/experiences/rollout-experience";
import { PERSONALIZATION_ROUTES } from "@src/routes/routes";
import { resetAudiences, resetRolloutsInfo } from "@src/store/slices/audiences";
import { resetExperience } from "@src/store/slices/experiences";
import { setDialog } from "@src/store/slices/global";
import { resetRedirects } from "@src/store/slices/redirects";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { experienceKeys } from "../queryKeys";
import { redirectKeys } from "../redirects/use-and-set-redirects-by-experience-id";
import { ApiRequest } from "../types/api";

const rolloutExperience = async (
  payload: ApiRequest<
    RolloutExperienceRequestBodyType,
    RolloutExperienceRequestPathParamType
  >
): Promise<RolloutExperienceResponseBodyType> => {
  const resp = await api.post<RolloutExperienceResponseBodyType>(
    `/v2/experiences/${payload.pathParams.experienceId}/rollout`,
    payload.body
  );
  return resp.data;
};

export const useRolloutExperience = (isExperienceRender: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  return useMutation<
    RolloutExperienceResponseBodyType,
    Error,
    ApiRequest<
      RolloutExperienceRequestBodyType,
      RolloutExperienceRequestPathParamType
    >
  >({
    mutationKey: ["rolloutExperience"],
    mutationFn: async (data) => rolloutExperience(data),
    onSettled: (data) => {
      dispatch(
        setDialog({ dialog: "rollouts", open: false, clearProps: true })
      );
      dispatch(resetRolloutsInfo());
      if (!isExperienceRender) {
        dispatch(resetAudiences());
        dispatch(resetRedirects());
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: experienceKeys.experimentList,
      });
      void queryClient.invalidateQueries({
        queryKey: experienceKeys.personalizationList,
      });

      void queryClient.invalidateQueries({ queryKey: redirectKeys.all });
      dispatch(resetAudiences());
      dispatch(resetRedirects());
      dispatch(resetExperience());

      if (data.experience) {
        void navigate(
          `${PERSONALIZATION_ROUTES.Personalization}/${data.experience.id}?action=edit&tab=${TabEnumFe.MODS}`,
          {
            state: { showDetailsDialog: true, isRoutedFromRollout: true },
          }
        );
      }
    },
  });
};
